/**
 * Orders page — list orders ที่บันทึกแล้ว
 * GET /api/admin/orders → { orders: [{ id, orderNo, status, total, createdAt }] }
 *
 * Status flow:
 *   draft → pending_slip → paid → shipped → delivered → completed
 *   (หรือ cancelled / refunded)
 */
import { useQuery } from '@tanstack/react-query';
import { ShoppingCart, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface Order {
  id: number;
  orderNo: string | null;
  status: string;
  total: number | null;
  createdAt: string;
}

async function fetchOrders() {
  const base = import.meta.env.VITE_API_BASE || 'https://cher-fb-bot.nayaa.workers.dev';
  const res = await fetch(`${base}/api/admin/orders`, { credentials: 'include' });
  if (!res.ok) throw new Error(`orders ${res.status}`);
  return res.json() as Promise<{ orders: Order[] }>;
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  draft: { label: 'ร่าง', color: 'bg-gray-100 text-gray-700' },
  pending_slip: { label: 'รอสลิป', color: 'bg-amber-100 text-amber-700' },
  paid: { label: 'จ่ายแล้ว', color: 'bg-blue-100 text-blue-700' },
  shipped: { label: 'ส่งแล้ว', color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'ถึงปลายทาง', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'สำเร็จ', color: 'bg-emerald-200 text-emerald-800' },
  cancelled: { label: 'ยกเลิก', color: 'bg-red-100 text-red-700' },
  refunded: { label: 'คืนเงิน', color: 'bg-orange-100 text-orange-700' },
};

function fmtBaht(satang: number | null) {
  if (satang == null) return '-';
  return (satang / 100).toLocaleString('th-TH', { minimumFractionDigits: 2 }) + ' ฿';
}

function fmtDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

export default function OrdersPage() {
  const q = useQuery({ queryKey: ['orders'], queryFn: fetchOrders, staleTime: 15_000 });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart size={20} />
          <h2 className="text-xl font-bold">ออเดอร์ทั้งหมด</h2>
        </div>
        <Button size="sm" variant="outline" onClick={() => q.refetch()}>
          <RefreshCw size={14} className={`mr-1 ${q.isFetching ? 'animate-spin' : ''}`} /> รีเฟรช
        </Button>
      </div>

      <div className="border border-blue-200 bg-blue-50/40 rounded-lg p-3 text-xs text-blue-800">
        💡 <strong>เมื่อบอตยืนยันออเดอร์กับลูกค้า → จะบันทึกเข้าตารางนี้อัตโนมัติ</strong> —
        แอดมินจัดการสถานะ + ส่งสลิป + ออกเลขพัสดุได้ที่นี่.
        สถานะอัพเดทอัตโนมัติเมื่อ slip verified / tracking ส่งมา.
      </div>

      {q.isLoading && <div className="text-sm text-muted-foreground">กำลังโหลด…</div>}
      {q.isError && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
          ❌ โหลดไม่สำเร็จ: {String(q.error)}
        </div>
      )}

      {q.data && q.data.orders.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <ShoppingCart size={48} className="mx-auto opacity-30 mb-3" />
          <div className="text-sm">ยังไม่มีออเดอร์ในระบบ</div>
          <div className="text-xs mt-1">เมื่อลูกค้ายืนยันซื้อกับบอต ออเดอร์จะแสดงที่นี่</div>
        </div>
      )}

      {q.data && q.data.orders.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th className="px-3 py-2 w-16">#</th>
                <th className="px-3 py-2">เลขออเดอร์</th>
                <th className="px-3 py-2 w-32">สถานะ</th>
                <th className="px-3 py-2 text-right w-32">ยอดรวม</th>
                <th className="px-3 py-2 w-44">วันที่</th>
              </tr>
            </thead>
            <tbody>
              {q.data.orders.map((o) => {
                const st = STATUS_LABEL[o.status] ?? { label: o.status, color: 'bg-gray-100' };
                return (
                  <tr key={o.id} className="border-t border-border hover:bg-muted/30">
                    <td className="px-3 py-2 font-mono text-xs">{o.id}</td>
                    <td className="px-3 py-2">{o.orderNo ?? <span className="text-muted-foreground">—</span>}</td>
                    <td className="px-3 py-2">
                      <Badge className={st.color}>{st.label}</Badge>
                    </td>
                    <td className="px-3 py-2 text-right font-medium">{fmtBaht(o.total)}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{fmtDate(o.createdAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
